<template>
  <div v-if="isLoading">
    <SkeletonLoader />
  </div>
  <div class="product-page" v-else>
    <div class="product-detail-container">
      <!-- Galeria de Imagens -->
      <div class="image-gallery">
        <div class="tab-content product-details-large">
          <div v-if="images.length > 0" v-for="(image, index) in images" :key="index"
            :class="['tab-pane fade', { 'show active': currentIndex === index }]" :id="'single-slide-' + (index + 1)"
            role="tabpanel" :aria-labelledby="'single-slide-tab-' + (index + 1)">
            <div class="single-product-img">
              <button class="prev-button" @click="prevImage">
                <i class="fas fa-chevron-left"></i>
              </button>
              <img :src="image.url" :alt="'Image ' + (index + 1)" class="img-fluid image-large"
                @click="openModal(index)" />
              <button class="next-button" @click="nextImage">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
          <div v-else class="tab-pane fade show active" id="no-image">
            <div class="single-product-img no-image-container">
              <img src="@/assets/images/produto-sem-imagem.png" :alt="t('product.noImage')"
                class="img-fluid no-image" />
            </div>
          </div>
        </div>

        <!-- Miniaturas -->
        <div class="single-product-menu">
          <div class="nav single-slide-menu" role="tablist">
            <div v-for="(image, index) in images" :key="index" class="single-tab-menu">
              <a data-bs-toggle="tab" :id="'single-slide-tab-' + (index + 1)" :href="'#single-slide-' + (index + 1)"
                @click="setCurrentIndex(index)">
                <img :src="image.url" :alt="'Thumbnail ' + (index + 1)" class="img-fluid thumbnail" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Informações -->
      <div class="product-info">
        <div class="brand-stock-container">
          <div class="brand-logo-container">
            <img v-if="logo2" :src="logo2" :alt="marca" class="brand-logo" />
            <span v-else class="brand-name">{{ marca }}</span>
          </div>
          <span v-if="temEstoque" class="stock-status in-stock">{{ t('product.available') }}</span>
          <span v-else class="stock-status out-of-stock">{{ t('product.unavailable') }}</span>
        </div>
        <h2 class="product-title no-margin">{{ titulo === nome ? titulo : titulo + ' ' + nome }}</h2>
        <p class="product-code tight-code">
          <span>{{ t('product.code') }}: </span>{{ codigo }}
        </p>



        <div class="product-info-details">
          <div class="price-buy-row">
            <h2 class="product-price-item">{{ product.preco_atual?.formatado }}</h2>
            <img v-if="temEstoque && product?.tem_peso" src="@/assets/svgs/comprar-btn.svg" alt="Comprar"
              class="comprar-svg-button" @click="adicionarAoCarrinho" />
          </div>
          <p class="iva-notice">
            {{ locale === 'pt-BR' ? t('product.taxNotice') : t('product.noTaxNotice') }}
          </p>
        </div>

        <img v-if="isPortuguese && !isMobile" src="@/assets/svgs/formas-pagamentos-desktop.svg"
          alt="Formas de pagamento" class="formas-pagamento-svg" />

        <img v-if="isPortuguese && isMobile" src="@/assets/svgs/formas-pagamentos-mobile.svg"
          alt="Formas de pagamento mobile" class="formas-pagamento-svg" />
        <p class="foreign-warning-mobile" v-if="isPortuguese && isMobile">
          <strong>Atenção:</strong> Não realizamos entregas no Brasil. Conclua sua compra online e
          realize a retirada ou agende instalação em nossa loja física, em Ciudad del Este - Paraguai.
        </p>
          <p :class="hasImage ? 'foreign-warning' : 'foreign-warning-static'" v-if="isPortuguese && !isMobile">
            <strong>Atenção:</strong> Não realizamos entregas no Brasil. Conclua sua compra online e
            realize a retirada ou agende instalação em nossa loja física, em Ciudad del Este - Paraguai.
          </p>
        <p class="product-description" v-html="detalhes"></p>
      </div>
    </div>

    <!-- Características -->
    <div class="caracteristica-container">
      <h2 class="caracteristica">{{ t('product.description') }}</h2>
      <p class="caracteristicas">{{ caracteristica }}</p>
    </div>

    <!-- Especificações -->
    <div v-if="hasEspecificacoes">
      <Especificacoes :data="especificacoes" />
    </div>

    <!-- Relacionados -->
    <section v-if="product?.marca_id" class="related-products-section">
      <Relacionados :marcaId="product.marca_id" />
    </section>

    <!-- Modal -->
    <div v-if="showModal" class="image-modal" aria-hidden="!showModal" @click="closeModal">
      <button class="close-button" @click.stop="closeModal">&times;</button>
      <button class="prev-button-modal" @click.stop="prevImageModal">
        <i class="fas fa-chevron-left"></i>
      </button>
      <img :src="images[currentIndex]?.url || '@/assets/produto-sem-imagem.png'" class="modal-image" />
      <button class="next-button-modal" @click.stop="nextImageModal">
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { eventBus } from '@/eventBus';
import { useStore } from 'vuex';
import ComprarBtn from '@/assets/svgs/comprar-btn.svg'
import SkeletonLoader from '@/components/produto/ProdutoSkeleton.vue';
import Relacionados from '@/components/produto/Relacionados.vue';
import Especificacoes from '@/components/produto/Especificacoes.vue';
import { fetchProductDetails, fetchBrands, fetchExchangeRates } from '@/services/apiService';

const { t, locale } = useI18n();
const isPortuguese = computed(() => store.getters['language/isPortuguese']);
const route = useRoute();
const store = useStore();
const isAnimating = ref(false);
const isLoading = ref(true);
const isMobile = ref(false);
const showModal = ref(false);
const currentIndex = ref(0);
const product = ref(null);
const marca = ref('');
const logo2 = ref('');
const exchangeRates = ref({});
const hasEspecificacoes = computed(() => product.value?.especificacoes?.length > 0);
const images = computed(() => product.value?.imagens || []);
const temEstoque = computed(() => product.value?.tem_estoque);
const titulo = computed(() => product.value?.titulo || '');
const nome = computed(() => product.value?.nome || '');
const codigo = computed(() => product.value?.codigo || '');
const detalhes = computed(() => product.value?.detalhes || '');
const caracteristica = computed(() => product.value?.caracteristica || '');
const especificacoes = computed(() => product.value?.especificacoes || []);
const hasImage = computed(() => images.value.length > 0);



const fetchBrandData = async (marcaId) => {
  try {
    const brands = await fetchBrands();
    const brand = brands.find((b) => b.id === marcaId);
    if (brand) {
      marca.value = brand.nome;
      logo2.value = brand.logo2 || '';
    }
  } catch (error) {
    console.error('Erro ao buscar dados da marca:', error);
  }
};
const adicionarAoCarrinho = () => {
  if (temEstoque.value && product.value?.tem_peso) {
    store.commit('ecommerce/ADD_TO_CART', {
      ...product.value,
      valor_venda: product.value.preco_atual?.valor_venda || 0,
    });
    eventBus.emit('toggle-cart');
    isAnimating.value = true;

    setTimeout(() => {
      isAnimating.value = false;
    }, 1200);
  } else {
    if (!temEstoque.value) {
      alert(t('outOfStock'));
    } else if (!product.value?.tem_peso) {
      alert(t('noWeight'));
    }
  }
};



const fetchProductData = async (slug) => {
  try {
    const data = await fetchProductDetails(slug);
    product.value = data;
    await fetchBrandData(data.marca_id);
  } catch (error) {
    console.error('Erro ao buscar dados do produto:', error);
  } finally {
    isLoading.value = false;
  }
};

const fetchRates = async () => {
  try {
    const rates = await fetchExchangeRates();
    exchangeRates.value = {
      BRL: parseFloat(rates.find(r => r.target_currency.name === 'Real')?.tax || 0),
      PYG: parseFloat(rates.find(r => r.target_currency.name === 'Guarani')?.tax || 0)
    };
  } catch (error) {
    console.error('Erro ao buscar taxas de câmbio:', error);
  }
};

const updateIsMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  window.scrollTo(0, 0);
  fetchProductData(route.params.slug);
  fetchRates();
  updateIsMobile();
  window.addEventListener('resize', updateIsMobile);
});

watch(() => route.params.slug, (newSlug) => {
  if (newSlug) {
    isLoading.value = true;
    fetchProductData(newSlug);
  }
});

const openModal = (index) => {
  currentIndex.value = index;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const setCurrentIndex = (index) => {
  currentIndex.value = index;
};

const prevImage = () => {
  currentIndex.value = (currentIndex.value - 1 + images.value.length) % images.value.length;
};

const nextImage = () => {
  currentIndex.value = (currentIndex.value + 1) % images.value.length;
};

const prevImageModal = () => {
  currentIndex.value = (currentIndex.value - 1 + images.value.length) % images.value.length;
};

const nextImageModal = () => {
  currentIndex.value = (currentIndex.value + 1) % images.value.length;
};
</script>


<style scoped>
body {
  font-family: 'Oswald', sans-serif;
}

.product-page {
  max-width: 1500px;
  margin: 0 auto;
  box-sizing: border-box;
  margin-top: 20px;
  justify-content: center;
}

h1,
h2,
h3,
h4,
h5,
h6,
p,
span,
div {
  font-family: 'Oswald', sans-serif;
}

.tab-content {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.descricao-container {
  background-color: #f9f9f9;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  margin-top: 20px;
}

.descricao {
  color: #ff5c5c;
  margin: 0;

}

.caracteristica-container {
  background-color: #f9f9f9;
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
}

.caracteristica {
  color: #ff5c5c;
  margin-top: 0.2rem;
  /* Pequena margem superior */
  margin-bottom: 0.5rem;
  /* Espaço controlado abaixo do título */
}

.brand-stock-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.brand-name {
  font-size: 2rem;
  font-weight: bold;
  color: #333;
}

.single-product-img {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.single-product-img img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-radius: 20px;
  cursor: pointer;
}

.brand-logo-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-logo {
  max-width: 150px;
  max-height: 150px;
  object-fit: contain;
  border-radius: 5px;
}

.add-to-cart-container {
  margin-top: 15px;
  width: 100%;
}

.add-to-cart-btn {
  background-color: #E23B33;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: auto;
  min-width: 140px;
}


.add-to-cart-btn.animate {
  animation: button-click 0.6s ease-in-out;
}

@keyframes button-click {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
}

.add-to-cart-btn:hover {
  background-color: #C5312B;
}

.add-to-cart-btn i {
  margin-right: 8px;
}




.prev-button,
.next-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #646464;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 6px;
  z-index: 2;
  border-radius: 5px;
  transition: background 0.3s ease;
}

.prev-button:hover,
.next-button:hover {
  background: #a3a3a3;
}

.prev-button {
  left: 10px;
}

.next-button {
  right: 10px;
}

.prev-button-modal,
.next-button-modal {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #a3a3a3;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 10px;
  z-index: 1000;
  border-radius: 20%;
  transition: background 0.3s ease;
}

.prev-button-modal:hover,
.next-button-modal:hover {
  background: #666666;
}

.prev-button-modal {
  left: 20px;
}

.next-button-modal {
  right: 20px;
}

.single-product-img img:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.single-product-menu {
  margin-top: 10px;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.no-margin {
  margin-bottom: 0.25rem;
}

.tight-code {
  margin-top: 0;
  font-size: 1rem;
  color: #777;
  margin-left: 2px;
}

.single-slide-menu {
  cursor: pointer;
}

.thumbnail {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  transition: transform 0.2s ease;
}

.thumbnail:hover {
  transform: scale(1.1);
}

.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-image {
  max-width: 90%;
  max-height: 80%;
  border-radius: 8px;
  position: relative;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 10px;
  border-radius: 10px;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}





.product-code {
  color: rgba(0, 0, 0, 0.6);
  font-size: 1.2rem;
  color: #777;
  flex: 1;
}



.product-price {
  font-size: 1.8rem;
  font-weight: 700;
  color: red;

}

.product-detail-container {
  display: flex;
  flex-direction: row-reverse;
  gap: 2rem;
  padding: 2rem;
  background-color: #f9f9f9;
  border-radius: 12px;
}

.image-gallery {
  flex: 1;
  max-width: 600px;

}

.product-info {
  flex: 1;
  padding: 1rem;
  border-radius: 10px;
  background-color: #f9f9f9;
  position: relative;
   min-height: 400px;
}

.product-title {
  font-size: 2.5rem;
  font-weight: bold;
}

.product-code-stock {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.foreign-warning {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  font-size: 1.02rem;
  color: rgb(194, 192, 192);
  background-color: transparent;
  padding: 10px;
  border-radius: 8px;
  margin-top: 10px;
  padding-top: 15px;
  text-align: center;
}



.product-prices {
  text-align: left;
  font-size: 2.5rem
}

.horizontal-layout {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* GARANTE centralização vertical */
  gap: 2rem;
  flex-wrap: wrap;
}

.price-block {
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* centra verticalmente dentro da coluna */
  flex: 1;
}

.buy-button-block {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 1;
  min-width: 200px;
}

.comprar-svg-button {
  width: 200px;
  height: auto;
  max-height: 64px;
  /* limite visual para alinhar com a altura da fonte */
  cursor: pointer;
  transition: transform 0.3s ease;
}

.formas-pagamento-svg {
  height: auto;
  max-height: 130px;
  cursor: pointer;
}

.comprar-svg-button:hover {
  transform: scale(1.05);
}


.additional-prices {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.thumbnail {
  max-width: 80px;
  max-height: 80px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.thumbnail:hover {
  transform: scale(1.05);
}

.no-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 500px;
}

.no-image {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}
@media (max-width: 768px) {
  .no-image-container {
    height: 300px;
  }
}

.product-description {
  font-size: 1rem;
  color: #555;
}

.caracteristicas {
  font-size: 1.6rem;
  font-family: 'Oswald', sans-serif;
  font-weight: 600;
  color: #5e5e5e;
  margin-left: 10px
}

.stock-details {
  font-size: 1rem;
  color: #333;
}

.stock-status {
  font-size: large;
  font-weight: 700;
  flex: 1;
  text-align: right;
  font-size: 2rem;
}

.in-stock {
  font-family: 'Oswald', sans-serif;
  font-weight: 600;

  color: #2acb6c;
  font-size: 2.4rem;
}



.out-of-stock {
  color: #dc3545;
  background-color: #f8d7da;
  padding: 0.5rem;
  border-radius: 5px;
  font-size: 2rem;
}


.price-container {
  display: flex;
  gap: 5px;
  align-items: flex-end;
}


.flag2 {
  width: 20px;
  height: 12px;
  margin-right: 5px;
}

.product-prices {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.price-left {
  flex: 1;
}

.product-price-item {
  font-size: 3.5rem;
  font-weight: 700;
  color: red;
  white-space: nowrap;
  opacity: 0.6;
}

.price-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}

.additional-prices {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  text-align: left;
  gap: 1px;
  align-items: flex-start;
}

.foreign-warning-mobile {
  display: none;
}

.foreign-warning-static {
  position: relative;
  margin-top: 20px;
  text-align: center;
  font-size: 1.02rem;
  color: rgb(194, 192, 192);
}

.product-price2 {
  font-size: 0.7rem;
  margin: 0;
  white-space: nowrap;
  line-height: 0.01;
  margin-bottom: 12px;
  color: rgba(0, 0, 0, 0.5);
  text-align: left;
}

.price-buy-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
}

.price-buy-row  {
  margin: 0;
}

.product-info-details {
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.iva-notice {
  font-size: 0.9rem;
  color: #c2c2c2;
  margin-left: 0;
  margin-top: 0.3rem;
  font-family: 'Oswald', sans-serif;
  font-weight: 600;
}

h2.block-title2,
.block-title2 h2 {
  font-size: 2rem;
  font-weight: 700;
  position: relative;
  padding-bottom: 5px;
  margin-bottom: 15px;
  line-height: 1.2;
  font-family: 'Lato', sans-serif;
}

h2.block-title2:before,
.block-title2 h2:before {
  position: absolute;
  bottom: 110%;
  left: 0;
  width: 100px;
  height: 3px;
  background: red;
  content: "";
}

h2.block-title2:after,
.block-title2 h2:after {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: #f4f4f4;
  content: "";
}


@media (max-width: 480px) {
  .add-to-cart-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .product-detail-container {
    flex-direction: column;
    gap: 1rem;
  }

  .iva-notice {
    font-size: 0.5rem;
    color: #c2c2c2;
    margin-left: 0;
    margin-top: -0.8rem;
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
  }

  .product-info {
    padding-bottom: 110px;
  }

  .product-code {
    font-size: 0.7rem;

  }

  .foreign-warning-mobile {
    display: block;
    font-size: 0.55rem;
    color: #b3b3b3;
    font-family: 'Oswald', sans-serif;
    font-weight: 400;
    text-align: center;
    margin-top: 0rem;
    margin-bottom: 0rem;
    line-height: 1.4;
    padding: 0 12px;
  }

  .in-stock {
    font-weight: 700;
    color: #2acb6c;
    font-size: 1.6rem !important;
  }

  .price-buy-row {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
    gap: 0.5rem;
    width: 100%;
  }

  .stock-status {
    font-size: 1.5rem;
  }

  .out-of-stock {
    font-size: 1.2rem;
    padding: 0.3rem;
  }

  .product-price-item {
    font-size: 2rem;
    white-space: nowrap;
  }

  .comprar-svg-button {
    max-width: 130px;
    max-height: 48px;
    width: auto;
    height: auto;
  }

  .formas-pagamento-svg {
    max-height: none;
    height: auto;
    width: 90%;
    margin: 1rem auto;
    display: block;
  }

  .foreign-warning {
    position: absolute;
    bottom: 5px;
    /* aproxima da borda inferior */
    left: 0;
    right: 0;
    padding: 8px 16px;
    font-size: 0.95rem;
    text-align: center;
    color: #b3b3b3;
    font-weight: 400;
    font-family: 'Oswald', sans-serif;
    letter-spacing: -0.3px;
    line-height: 1.4;
  }


  .skeleton-container {
    padding: 0 5px;
  }

  .image-gallery {
    max-width: 100%;
    width: 100%;
    margin: 0 auto;
  }

  .horizontal-layout {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .buy-button-block {
    justify-content: center;
  }

  .image-gallery {
    height: 50vh;
    /* Ajuste para telas menores */
  }

  .single-product-img img {
    border-radius: 10px;
  }

  .product-title {
    font-size: 1.5rem;
  }

  .product-price {
    font-size: 1.5rem;
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    color: #FF5C5C;
  }


  .single-product-menu {
    display: none;
  }

  .image-gallery .tab-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .prev-button,
  .next-button {
    font-size: 1rem;
    padding: 6px;
  }

  .product-prices {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }



  .product-info-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

}

@media (max-width: 480px) {
  .product-title {
    font-size: 1.5rem;
  }

  .image-gallery {
    height: 40vh;
  }

  .caracteristicas {
    font-size: 1rem;
  }

  .single-product-img img {
    border-radius: 5px;
  }

  .product-price {
    font-size: 1.5rem;
  }

  .thumbnail {
    width: 60px;
    height: 60px;
  }

  .product-price-item {
    margin-right: 10px;
  }

  .close-button {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }

  .product-prices {
    gap: 10px;
  }

  .product-info-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    margin-top: 20px;
  }

  .product-page {
    max-width: 1500px;
    margin: 0 auto;
    box-sizing: border-box;
    margin-top: 5px
  }

}
</style>
